import React, { ReactElement, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Link } from "react-router-dom";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineFileSearch } from "react-icons/ai";
import { MdOutlineContentCopy } from "react-icons/md";
import AgGridPagination from "../../shared/AgGridPagination";

ModuleRegistry.registerModules([AllCommunityModule]);

type RowData = {
  id: string;
  name: string;
  support_email: string;
  is_active: string;
};

const MerchantList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
}) => {
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  interface MerchantStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: MerchantStatusItems[] = [
    {
      status: "yes",
      value: "yes",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
    {
      status: "no",
      value: "no",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
  ];

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.data.is_active;
    const statusItem = statusList.find((item) => item.status === status);
    return <>{statusItem ? statusItem.display : null}</>;
  };

  const ActionsRenderer = (params: ICellRendererParams) => (
    <Tooltip title="View More Details" arrow placement="right">
      <div>
        <IconButton
          component={Link}
          to={`/${userRole}/merchants/manage/${params.data.id}`}
          id={"button-detail-" + params.data?.id}
          size="small"
        >
          <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "name",
      headerName: "Name",
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: "support_email",
      headerName: "Support Email",
      sortable: false,
      minWidth: 140,
      flex: 1,
    },
    {
      field: "is_active",
      headerName: "API Status",
      sortable: false,
      width: 120,
      cellRenderer: StatusRenderer,
      cellClass: "ag-cell-status",
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 80,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <Box className="card w-full">
      <div style={{ width: "100%" }}>
        <AgGridReact
          theme={themeAlpine}
          rowData={isLoading ? [] : tableData}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.id}
          rowHeight={48}
          headerHeight={44}
          domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          suppressPaginationPanel={true}
          loading={isLoading}
          loadingOverlayComponent={CustomLoadingOverlay}
          overlayNoRowsTemplate="No data available"
        />
      </div>

      <AgGridPagination
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        onPaginationChange={setPaginationModel}
        hidden={hidePagination}
      />
    </Box>
  );
};

export default MerchantList;
